import { UpgradeToPro } from '../../../../../componentsV2/UpgradeToPro';
import { Button, DatePicker } from 'antd';
import { CalendarOutlined, ClearOutlined, CopyFilled, SendOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useToast } from '../../../../../hooks/useToast';
import { useAskMeAnything } from '../../../../askMeAnything/hooks/useAskMeAnything';
import { ChatItem } from '../../../../../services/types';
import { MessagesList } from './MessagesList';
import useFeatures from '../../../../../hooks/useFeatures';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
import Styles from './styles';
import { useAccountSearch } from '../../../../account/account-search/account-search.hook';
import { useAnalytics } from '../../../../analytics/hooks/useAnalytics';
const { ChatContainer, ChatInput3, ChatActionsContainer, Chat, OneLinerContainer } = Styles;

interface Props {
  fullMessageBubble: boolean;
  askFunction: (
    chatMessages: ChatItem[],
    from?: Date,
    to?: Date,
  ) => Promise<ReadableStream<Uint8Array> | null>;
  location: string;
  styles?: React.CSSProperties;
}

export const AskMeAnythingChat3: React.FC<Props> = ({
  askFunction,
  location,
  styles,
  fullMessageBubble,
}) => {
  const { sendEvent } = useAnalytics();
  const { accounts } = useAccountSearch();

  const { success } = useToast();
  const { messages, isAsking, ask, clearMessages, copyAll } = useAskMeAnything();

  const [enableSelectDates, setEnableSelectDates] = useState(false);
  const [calendarValues, setCalendarValues] = useState<[Date | null, Date | null]>([null, null]);

  const noMessages = messages.length === 0;

  const features = useFeatures();
  const availableFeature = features.ama;

  const [question, setQuestion] = useState('');

  const [accountOptions, setAccountOptions] = useState<
    {
      key: string;
      value: string;
      label: string;
    }[]
  >([]);

  const sendMessage = () => {
    if (!isAsking) {
      ask({
        question: question,
        askFunction: askFunction,
        location: location,
        from: enableSelectDates ? calendarValues[0] || undefined : undefined,
        to: enableSelectDates ? calendarValues[1] || undefined : undefined,
      });
      setQuestion('');
      sendEvent('ai search');
    }
  };

  const handleCopyAll = () => {
    copyAll();
    success('Messages copied to clipboard');
  };

  const handleSearch = (searchText: string) => {
    const filteredAccounts = accounts
      .filter(account => account.name.toLowerCase().includes(searchText.toLowerCase()))
      .map(({ id, name }) => ({
        key: id,
        value: name,
        label: name,
      }));
    setAccountOptions(filteredAccounts);
  };

  return (
    <UpgradeToPro plan="pro" display={!availableFeature}>
      <Chat style={styles}>
        <MessagesList messages={messages} fullMessageBubble={fullMessageBubble} />

        <ChatActionsContainer>
          <OneLinerContainer>
            <Button
              disabled={noMessages}
              icon={<CopyFilled />}
              onClick={handleCopyAll}
              style={{ borderRadius: '8px' }}
            >
              Copy All
            </Button>

            <Button
              disabled={noMessages}
              icon={<ClearOutlined />}
              onClick={clearMessages}
              style={{ borderRadius: '8px' }}
            >
              Clear Chat
            </Button>

            <Button
              icon={<CalendarOutlined />}
              onClick={() => setEnableSelectDates(!enableSelectDates)}
              style={{
                borderColor: enableSelectDates ? '#FF6C54' : '#E0E6EE',
                color: enableSelectDates ? '#FF6C54' : 'initial',
              }}
            >
              Select Dates
            </Button>
          </OneLinerContainer>

          {enableSelectDates && (
            <OneLinerContainer>
              <RangePicker
                maxDate={dayjs()}
                onCalendarChange={event => {
                  setCalendarValues([event[0]?.toDate() || null, event[1]?.toDate() || null]);
                }}
              />
            </OneLinerContainer>
          )}

          <ChatContainer>
            <ChatInput3
              placeholder="Type your message..."
              prefix="@"
              split=" "
              rows={1}
              value={question}
              onChange={(value: string) => setQuestion(value)}
              onSearch={handleSearch}
              onKeyDown={ev => {
                if (ev.key === 'Enter' && !ev.shiftKey) {
                  ev.stopPropagation();
                  ev.preventDefault();
                  sendMessage();
                }
              }}
              options={accountOptions}
              style={{ width: '100%' }}
            />

            <Button
              onClick={sendMessage}
              icon={<SendOutlined />}
              size="small"
              style={{
                borderRadius: '8px',
                position: 'absolute',
                right: '16px',
                top: '8px',
                padding: '0',
              }}
              disabled={!question || isAsking}
              type="primary"
              loading={isAsking}
            />
          </ChatContainer>
        </ChatActionsContainer>
      </Chat>
    </UpgradeToPro>
  );
};
